import { BookOpen, Lightbulb, Layers, Clock } from "lucide-react";
import type { CatalogItem } from "../types/navigator";
import { sensitivityInfo } from "../utils/platformSensitivity";
import { LikeButton } from "./LikeButton";
import {
  Badge,
  assetVariant,
  complexityVariant,
  priorityVariant,
  scoreVariant,
  statusVariant,
} from "./Badge";
import { Avatar } from "./Avatar";
import { isNew, isRecentlyUpdated, relativeTime } from "../utils/time";
import { round2 } from "../utils/score";
import type { ViewMode } from "./ViewToggle";

type Props = {
  item: CatalogItem;
  onOpen: (item: CatalogItem) => void;
  view?: ViewMode;
};

function KindIcon({ kind, size = 16 }: { kind: CatalogItem["kind"]; size?: number }) {
  if (kind === "library") return <BookOpen size={size} />;
  if (kind === "initiative") return <Lightbulb size={size} />;
  return <Layers size={size} />;
}

function ownerNameFor(item: CatalogItem): string | null {
  if (item.kind === "initiative") return item.submittedBy || null;
  if (item.kind === "library") return item.department || null;
  return null;
}

function ownerLabelFor(item: CatalogItem): string | null {
  if (item.kind === "initiative") return "Submitted by";
  if (item.kind === "library") return "Department";
  return null;
}

function CardBadges({ item }: { item: CatalogItem }) {
  if (item.kind === "library") {
    return (
      <>
        {item.aiPlatform ? <Badge variant="outline">{item.aiPlatform}</Badge> : null}
        {item.complexity ? <Badge variant={complexityVariant(item.complexity)}>{item.complexity}</Badge> : null}
      </>
    );
  }
  if (item.kind === "initiative") {
    return (
      <>
        {item.priority ? <Badge variant={priorityVariant(item.priority)}>{item.priority} priority</Badge> : null}
        {item.region ? <Badge variant="outline">{item.region}</Badge> : null}
      </>
    );
  }
  return (
    <>
      {item.platformType ? <Badge variant="outline">{item.platformType}</Badge> : null}
      {item.complexity ? <Badge variant={complexityVariant(item.complexity)}>{item.complexity}</Badge> : null}
    </>
  );
}

// "New" wins over "Updated" — never show both.
function ribbonFor(item: CatalogItem): "new" | "updated" | null {
  // A "New" badge next to a status that already reads "New" looks like a
  // duplicate, so let the status speak for itself.
  if (isNew(item.created)) return (item.status ?? "").trim().toLowerCase() === "new" ? null : "new";
  if (isRecentlyUpdated(item.created, item.modified)) return "updated";
  return null;
}

export function ItemCard({ item, onOpen, view = "grid" }: Props) {
  const ribbon = ribbonFor(item);
  const ownerName = ownerNameFor(item);
  const ownerLabel = ownerLabelFor(item);
  const relTime = relativeTime(item.modified ?? item.created);
  const kindClass = `card-${item.kind}`;
  const sensitivity = item.kind === "platform" ? sensitivityInfo(item) : null;

  if (view === "compact") {
    return (
      <button
        className={`card card-compact ${kindClass}`}
        onClick={() => onOpen(item)}
        aria-label={`Open ${item.title}`}
      >
        <span className="card-accent" aria-hidden />
        <span className={`card-icon icon-${item.kind}`} aria-hidden>
          <KindIcon kind={item.kind} size={14} />
        </span>
        <span className="card-compact-title">{item.title}</span>
        {sensitivity ? (
          <Badge variant={sensitivity.variant}>
            <sensitivity.icon size={11} aria-hidden /> {sensitivity.label}
          </Badge>
        ) : item.status ? (
          <Badge variant={statusVariant(item.status)}>{item.status}</Badge>
        ) : null}
        {ownerName ? (
          <span className="card-compact-owner">
            <Avatar name={ownerName} size={18} title={`${ownerLabel}: ${ownerName}`} />
            <span className="card-compact-owner-name">{ownerName}</span>
          </span>
        ) : null}
        {relTime ? (
          <span className="card-compact-time" title={item.modified ?? item.created}>
            <Clock size={11} aria-hidden /> {relTime}
          </span>
        ) : null}
        {ribbon ? (
          <span className={`card-ribbon-inline card-ribbon-${ribbon}`}>
            {ribbon === "new" ? "New" : "Updated"}
          </span>
        ) : null}
      </button>
    );
  }

  return (
    <button
      className={`card ${kindClass}`}
      onClick={() => onOpen(item)}
      aria-label={`Open ${item.title}`}
    >
      <span className="card-accent" aria-hidden />
      <div className="card-head">
        <div className="card-title-row">
          <div className={`card-icon icon-${item.kind}`} aria-hidden>
            <KindIcon kind={item.kind} size={16} />
          </div>
          <div className="card-title">{item.title}</div>
          {item.kind === "initiative" ? (() => {
            const score = round2((parseFloat(item.businessValueScore) || 0) + (parseFloat(item.technicalFeasibilityScore) || 0));
            return score > 0 ? (
              <span className="card-status">
                <Badge variant={scoreVariant(score)}>{score}</Badge>
              </span>
            ) : null;
          })() : item.kind === "library" ? (
            <span className="card-status">
              <LikeButton item={item} />
            </span>
          ) : sensitivity ? (
            <span className={`card-sensitivity-icon ${sensitivity.cls}`} aria-hidden>
              <sensitivity.icon size={24} />
            </span>
          ) : null}
        </div>
        {item.description ? <div className="card-desc">{item.description}</div> : null}
      </div>

      {item.kind === "library" ? (() => {
        const createdBy = (item.raw._createdByName as string) || "";
        return (item.department || createdBy) ? (
          <div className="card-owner">
            {item.department ? (
              <Avatar name={item.department} size={22} title={`Department: ${item.department}`} />
            ) : null}
            {item.department ? (
              <div className="card-owner-text">
                <span className="card-owner-label">Department</span>
                <span className="card-owner-name">{item.department}</span>
              </div>
            ) : null}
            {createdBy ? (
              <div className="card-owner-text" style={{ borderLeft: item.department ? "1px solid var(--border)" : undefined, paddingLeft: item.department ? 10 : undefined }}>
                <span className="card-owner-label">Submitted by</span>
                <span className="card-owner-name">{createdBy}</span>
              </div>
            ) : null}
          </div>
        ) : null;
      })() : ownerName ? (
        <div className="card-owner">
          <Avatar name={ownerName} size={22} title={`${ownerLabel}: ${ownerName}`} />
          <div className="card-owner-text">
            <span className="card-owner-label">{ownerLabel}</span>
            <span className="card-owner-name">{ownerName}</span>
          </div>
        </div>
      ) : null}

      <div className="card-foot">
        <div className="card-foot-badges">
          <CardBadges item={item} />
        </div>
        <div className="card-foot-meta">
          {item.kind === "library" && item.assetType ? (
            <Badge variant={assetVariant(item.assetType)}>{item.assetType}</Badge>
          ) : null}
          {item.kind === "initiative" && item.status ? (
            <Badge variant={statusVariant(item.status)}>{item.status}</Badge>
          ) : null}
          {item.kind === "platform" && sensitivity ? (
            <Badge variant={sensitivity.variant}>{sensitivity.label}</Badge>
          ) : null}
        </div>
      </div>
    </button>
  );
}
